<template>
    <transition name="activation-script-modal">
        <div v-if="scenario" class="activation-overlay" @click.self="$emit('close')">
            <section class="activation-modal" role="dialog" aria-modal="true">
                <header class="act-head">
                    <div class="act-head-text">
                        <div class="act-kicker">
                            <span class="act-dot"></span>
                            Авторассылка
                        </div>
                        <h2 class="act-title">{{ scenario.name }}</h2>
                        <p class="act-sub">{{ scenario.meta.description }}</p>
                    </div>
                    <button class="act-x" type="button" aria-label="Закрыть" @click="$emit('close')">
                        &times;
                    </button>
                </header>

                <main class="act-main">
                    <section class="act-block">
                        <div class="act-block-head">
                            <div>
                                <div class="act-block-title">Условия активации</div>
                                <div class="act-block-sub">
                                    В активацию попадают клиенты, удовлетворяющие условиям:
                                </div>
                            </div>
                        </div>

                        <div :class="['act-conditions', conditionLayoutClass]">
                            <div
                                v-for="condition in conditions"
                                :key="condition.key"
                                :class="['act-cond', `act-cond--${condition.type}`]"
                            >
                                <template v-if="condition.type === 'range-card'">
                                    <div class="act-cond-label">
                                        {{ condition.label }}
                                        <span v-if="condition.sub" class="act-cond-sub">/ {{ condition.sub }}</span>
                                    </div>
                                    <div class="act-range">
                                        <span>от</span>
                                        <input
                                            v-model.number="scenario.settings[condition.fromKey]"
                                            class="act-num"
                                            type="number"
                                            min="0"
                                            step="1"
                                        />
                                        <span>до</span>
                                        <input
                                            v-model.number="scenario.settings[condition.toKey]"
                                            class="act-num"
                                            type="number"
                                            min="0"
                                            step="1"
                                        />
                                    </div>
                                </template>

                                <template v-else-if="condition.type === 'range-line'">
                                    <span class="act-line-label">{{ condition.label }}:</span>
                                    <span class="act-range act-range--line">
                                        <span>от</span>
                                        <input
                                            v-model.number="scenario.settings[condition.fromKey]"
                                            class="act-num"
                                            type="number"
                                            min="0"
                                            step="1"
                                        />
                                        <span>до</span>
                                        <input
                                            v-model.number="scenario.settings[condition.toKey]"
                                            class="act-num"
                                            type="number"
                                            min="0"
                                            step="1"
                                        />
                                    </span>
                                </template>

                                <template v-else-if="condition.type === 'masters'">
                                    <span class="act-line-label">{{ condition.label }}:</span>
                                    <StarterPackEmployeePicker
                                        :scenario="scenario"
                                        :selected-staff="selectedStaff"
                                        :available-staff="availableStaff"
                                        :open-employee-menu-scenario-id="openEmployeeMenuScenarioId"
                                        @toggle-menu="$emit('toggle-employee-menu')"
                                        @pick="$emit('pick-employee', $event)"
                                        @remove="$emit('remove-master', $event)"
                                    />
                                </template>

                                <template v-else>
                                    <div class="act-cond-label">{{ condition.label }}</div>
                                    <div :class="['act-cond-value', { 'act-cond-value--pill': condition.pill }]">
                                        {{ condition.value }}
                                    </div>
                                </template>
                            </div>
                        </div>
                    </section>

                    <div v-if="scenario.meta.manualNote" class="act-manual-note">
                        <span class="act-manual-icon">!</span>
                        <span>{{ scenario.meta.manualNote }}</span>
                    </div>

                    <section
                        v-for="part in groupedParts"
                        :id="`starterpack-script-part-${part.partNumber}`"
                        :key="part.partNumber"
                        class="act-block"
                    >
                        <div class="act-section-title">
                            <span class="act-part-num">{{ part.partNumber }} часть</span>
                            <span class="act-part-name">{{ part.title }}</span>
                            <span class="act-part-hint">{{ part.messageParts.length }} {{ variantLabel(part.messageParts.length) }}</span>
                        </div>

                        <div class="act-variants">
                            <article
                                v-for="messagePart in part.messageParts"
                                :key="partKey(messagePart)"
                                :class="[
                                    'act-script-card',
                                    { 'act-script-card--invalid': isMessagePartEmpty(messagePart) },
                                ]"
                            >
                                <div class="act-variant-label">Вариант {{ messagePart.variantNumber }}</div>
                                <div class="act-text-wrap">
                                    <div class="act-script-label">Текст</div>
                                    <textarea
                                        v-model="messagePart.textVariant"
                                        class="act-textarea"
                                        spellcheck="true"
                                        rows="2"
                                        @input="autoGrow($event.target)"
                                    ></textarea>
                                </div>
                                <div class="act-tools">
                                    <button
                                        type="button"
                                        class="act-tool"
                                        title="Переменные"
                                        @click.stop="toggleVariables(messagePart)"
                                    >
                                        (x)
                                    </button>
                                    <div v-if="openVariablesKey === partKey(messagePart)" class="act-vars-popover">
                                        <button
                                            v-for="variable in allowedVariables"
                                            :key="variable"
                                            type="button"
                                            class="act-var-option"
                                            @click="insertVariable(messagePart, variable)"
                                        >
                                            {{ variable }}
                                        </button>
                                    </div>
                                </div>
                            </article>
                        </div>
                    </section>
                </main>

                <footer class="act-footer">
                    <div class="act-foot-note">
                        {{ validationHint }}
                    </div>
                    <div class="act-actions">
                        <button class="act-ghost" type="button" @click="$emit('close')">Отмена</button>
                        <v-tooltip bottom :disabled="applyTooltipDisabled">
                            <template v-slot:activator="{ on, attrs }">
                                <span class="act-primary-wrap" v-bind="attrs" v-on="on">
                                    <button
                                        class="act-primary"
                                        type="button"
                                        :disabled="applyDisabled"
                                        @click="apply"
                                    >
                                        {{ launching ? 'Применяем...' : 'Применить' }}
                                    </button>
                                </span>
                            </template>
                            <span>{{ applyTooltip }}</span>
                        </v-tooltip>
                    </div>
                </footer>
            </section>
        </div>
    </transition>
</template>

<script>
import StarterPackEmployeePicker from './StarterPackEmployeePicker.vue'
import { STARTER_PACK_SAVE_ACCESS_DENIED_HINT } from '../constants'
import { hasEmptyScriptMessagePart } from '../utils/scenarioSettings'

const ALLOWED_VARIABLES = [
    '{имя-клиента}',
    '{название-салона}',
    '{контактный-телефон}',
    '{адрес-салона}',
]

const PART_TITLES = {
    6: {
        1: 'Приветствие',
        2: 'Знакомство с услугами и подарок',
        3: 'Финальный вопрос',
    },
    7: {
        1: 'Приветствие администратора',
        2: 'Повод для записи',
    },
    8: {
        1: 'Приветствие',
        2: 'Запрос отзыва',
        3: 'Подпись',
    },
    9: {
        1: 'Приветствие',
        2: 'Сообщение об отпуске мастера',
        3: 'Финальный вопрос и ссылка',
    },
    10: {
        1: 'Приветствие',
        2: 'Приглашение записаться снова',
        3: 'Финальный вопрос',
    },
    14: {
        1: 'Приветствие',
        2: 'Текст активации со скидкой',
        3: 'Финальный вопрос',
    },
    15: {
        1: 'Приветствие',
        2: 'Основной текст',
        3: 'Финальный вопрос',
    },
}

export default {
    name: 'StarterPackActivationScriptModal',
    components: { StarterPackEmployeePicker },
    props: {
        scenario: {
            type: Object,
            default: null,
        },
        staff: {
            type: Array,
            default: () => [],
        },
        launching: {
            type: Boolean,
            default: false,
        },
        canSaveSettings: {
            type: Boolean,
            default: false,
        },
        openEmployeeMenuScenarioId: {
            type: [Number, String, null],
            default: null,
        },
    },
    data() {
        return {
            openVariablesKey: null,
        }
    },
    computed: {
        allowedVariables() {
            return ALLOWED_VARIABLES
        },
        selectedStaff() {
            const selectedIds = this.scenario?.settings?.masterIds || []
            return this.staff.filter(item => selectedIds.includes(item.id))
        },
        availableStaff() {
            const selectedIds = this.scenario?.settings?.masterIds || []
            return this.staff.filter(item => !selectedIds.includes(item.id))
        },
        messageParts() {
            return Array.isArray(this.scenario?.settings?.messageParts)
                ? this.scenario.settings.messageParts
                : []
        },
        groupedParts() {
            const grouped = this.messageParts.reduce((acc, part) => {
                const partNumber = Number(part.partNumber)
                if (!acc[partNumber]) {
                    acc[partNumber] = []
                }
                acc[partNumber].push(part)
                return acc
            }, {})

            return Object.keys(grouped)
                .map(Number)
                .sort((a, b) => a - b)
                .map(partNumber => ({
                    partNumber,
                    title: this.getPartTitle(partNumber),
                    messageParts: grouped[partNumber].sort(
                        (a, b) => Number(a.variantNumber) - Number(b.variantNumber)
                    ),
                }))
        },
        conditions() {
            const id = this.scenario?.scenarioId
            if (id === 6) {
                return [
                    {
                        key: 'days',
                        type: 'range-card',
                        label: 'Был в салоне',
                        sub: 'дней назад',
                        fromKey: 'daysFrom',
                        toKey: 'daysTo',
                    },
                    { key: 'visits', type: 'readonly', label: 'Посещений', value: '1 визит', pill: true },
                    { key: 'future', type: 'readonly', label: 'Будущая запись', value: 'отсутствует' },
                ]
            }
            if (id === 7) {
                return [
                    { key: 'segment', type: 'readonly', label: 'Сегмент', value: 'готовые записаться сейчас' },
                    { key: 'period', type: 'readonly', label: 'Период формирования', value: 'ежедневно', pill: true },
                    { key: 'future', type: 'readonly', label: 'Будущая запись', value: 'отсутствует' },
                ]
            }
            if (id === 8) {
                return [
                    { key: 'segment', type: 'readonly', label: 'Сегмент', value: 'первый визит к новому мастеру' },
                ]
            }
            if (id === 14) {
                return [
                    {
                        key: 'days',
                        type: 'range-card',
                        label: 'Должен был вернуться',
                        sub: 'дней назад',
                        fromKey: 'daysFrom',
                        toKey: 'daysTo',
                    },
                    { key: 'future', type: 'readonly', label: 'Будущая запись', value: 'отсутствует' },
                ]
            }
            if (id === 15) {
                return [
                    {
                        key: 'days',
                        type: 'range-card',
                        label: 'Ожидаемая дата визита',
                        sub: 'дней назад',
                        fromKey: 'daysFrom',
                        toKey: 'daysTo',
                    },
                ]
            }
            if (id === 10) {
                return [
                    { key: 'cancelled', type: 'readonly', label: 'Отменил запись', value: 'от 2 до 7 дней назад' },
                    { key: 'period', type: 'readonly', label: 'Период формирования', value: 'ежедневно', pill: true },
                ]
            }
            if (id === 9) {
                return [
                    {
                        key: 'visits',
                        type: 'range-line',
                        label: 'Количество посещений в салон/клинику',
                        fromKey: 'visitsFrom',
                        toKey: 'visitsTo',
                    },
                    { key: 'masters', type: 'masters', label: 'Мастер в отпуске' },
                ]
            }
            return []
        },
        conditionLayoutClass() {
            return this.conditions.some(condition => condition.type === 'range-line' || condition.type === 'masters')
                ? 'act-conditions--stack'
                : `act-conditions--grid act-conditions--${this.conditions.length || 1}`
        },
        hasEmptyMessagePart() {
            return hasEmptyScriptMessagePart(this.messageParts)
        },
        applyDisabled() {
            return this.launching || !this.canSaveSettings || this.hasEmptyMessagePart
        },
        applyTooltipDisabled() {
            return !this.applyDisabled || this.launching
        },
        applyTooltip() {
            if (!this.canSaveSettings) {
                return STARTER_PACK_SAVE_ACCESS_DENIED_HINT
            }
            if (this.hasEmptyMessagePart) {
                return 'Заполните все варианты всех частей скрипта'
            }
            return ''
        },
        validationHint() {
            if (this.hasEmptyMessagePart) {
                return 'Заполните все варианты всех частей скрипта.'
            }
            return 'Измените условия и тексты, затем нажмите «Применить».'
        },
    },
    watch: {
        scenario() {
            this.openVariablesKey = null
            // Тексты приходят в v-model до отрисовки, поэтому высоту полей
            // пересчитываем после того, как новый сценарий попал в DOM.
            this.$nextTick(() => this.autoGrowAll())
        },
    },
    mounted() {
        this.autoGrowAll()
    },
    methods: {
        autoGrow(el) {
            if (!el) {
                return
            }
            el.style.height = 'auto'
            el.style.height = `${el.scrollHeight}px`
        },
        autoGrowAll() {
            if (!this.scenario || typeof this.$el?.querySelectorAll !== 'function') {
                return
            }
            this.$el.querySelectorAll('.act-textarea').forEach(node => this.autoGrow(node))
        },
        getPartTitle(partNumber) {
            return PART_TITLES[this.scenario?.scenarioId]?.[partNumber] || 'Текст сообщения'
        },
        partKey(part) {
            return `${part.partNumber}:${part.variantNumber}`
        },
        isMessagePartEmpty(part) {
            return String(part?.textVariant || '').trim() === ''
        },
        variantLabel(count) {
            const normalized = Math.abs(Number(count)) % 100
            const last = normalized % 10
            if (normalized > 10 && normalized < 20) {
                return 'вариантов'
            }
            if (last === 1) {
                return 'вариант'
            }
            if (last > 1 && last < 5) {
                return 'варианта'
            }
            return 'вариантов'
        },
        toggleVariables(part) {
            const key = this.partKey(part)
            this.openVariablesKey = this.openVariablesKey === key ? null : key
        },
        insertVariable(part, variable) {
            part.textVariant = `${part.textVariant || ''}${variable}`
            this.openVariablesKey = null
            // Текст изменён программно, события input не будет.
            this.$nextTick(() => this.autoGrowAll())
        },
        apply() {
            if (this.applyDisabled) {
                return
            }
            this.$emit('launch')
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/vars.sass';

.activation-overlay {
    position: fixed;
    inset: 0;
    z-index: 1100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 22px;
    background: rgba(26, 26, 46, 0.46);
}

.activation-modal {
    display: flex;
    flex-direction: column;
    width: min(720px, 94vw);
    height: 760px;
    max-height: 92vh;
    overflow: hidden;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 18px;
    box-shadow: 0 22px 70px rgba(26, 26, 46, 0.28);
}

.act-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    padding: 20px 24px 16px;
    background: linear-gradient(180deg, #fff 0%, #fafbfd 100%);
    border-bottom: 1px solid $border-new;
}

.act-head-text {
    min-width: 0;
}

.act-kicker {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 7px;
    color: $main-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.act-dot {
    width: 7px;
    height: 7px;
    background: $main-new;
    border-radius: 50%;
    box-shadow: 0 0 0 3px $bcg-card-new;
}

.act-title {
    margin: 0;
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 23px;
    font-weight: 900;
    letter-spacing: 0;
    line-height: 1.1;
}

.act-sub {
    max-width: 640px;
    margin: 7px 0 0;
    color: $muted-new;
    font-size: 13px;
    line-height: 1.45;
}

.act-x {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    color: $muted-new;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    background: #f4f6fa;
    border: 1px solid $border-new;
    border-radius: 10px;
}

.act-main {
    flex: 1;
    min-height: 0;
    padding: 18px 20px 24px;
    overflow: auto;
    background: #fbfcfe;
}

.act-block {
    padding: 16px;
    margin-bottom: 14px;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 15px;
    box-shadow: 0 1px 3px rgba(26, 26, 46, 0.03);
}

.act-block:last-child {
    margin-bottom: 0;
}

.act-block-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
}

.act-block-title {
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 17px;
    font-weight: 900;
    letter-spacing: 0;
}

.act-block-sub {
    margin-top: 4px;
    color: $muted-new;
    font-size: 12.5px;
    line-height: 1.45;
}

.act-conditions--grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
}

.act-conditions--2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.act-conditions--1 {
    grid-template-columns: 1fr;
}

.act-conditions--stack {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.act-cond {
    color: $text-color-new;
}

.act-cond--readonly,
.act-cond--range-card {
    display: grid;
    align-content: start;
    min-height: 80px;
    padding: 12px 14px 10px;
    background: #fafbfd;
    border: 1px solid $border-light-new;
    border-radius: 12px;
    gap: 8px;
}

.act-cond--range-line,
.act-cond--masters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 10px;
    font-size: 13px;
    line-height: 1.5;
}

.act-cond-label {
    color: $muted-light-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.08em;
    line-height: 1.25;
    text-transform: uppercase;
}

.act-cond-sub {
    color: #9aa3b2;
    font-size: 8.5px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
}

.act-line-label {
    color: $text-color-new;
    font-weight: 500;
}

.act-range {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: $text-color-new;
    font-size: 13px;
}

.act-range--line {
    flex-wrap: wrap;
}

.act-num {
    width: 48px;
    height: 32px;
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 900;
    text-align: center;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 8px;
    outline: none;
}

.act-num:focus {
    border-color: $main-new;
    box-shadow: 0 0 0 3px rgba(82, 90, 213, 0.12);
}

.act-cond-value {
    display: flex;
    align-items: center;
    min-height: 28px;
    color: $text-color-new;
    font-size: 13px;
    line-height: 1.25;
}

.act-cond-value--pill {
    align-self: start;
    min-height: 30px;
    padding: 4px 9px;
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-weight: 800;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 8px;
}

.act-manual-note {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 10px 13px;
    margin-bottom: 14px;
    color: #8a6212;
    font-size: 12px;
    line-height: 1.45;
    background: #fff6e8;
    border: 1px solid #f6d69a;
    border-radius: 10px;
}

.act-manual-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    background: #f2a615;
    border-radius: 50%;
}

.act-section-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
}

.act-part-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 70px;
    padding: 6px 10px;
    color: #fff;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    background: $main-new;
    border-radius: 999px;
}

.act-part-name {
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 16px;
    font-weight: 900;
    letter-spacing: 0;
}

.act-part-hint {
    margin-left: auto;
    color: $muted-light-new;
    font-size: 12px;
}

.act-variants {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.act-script-card {
    position: relative;
    display: grid;
    grid-template-columns: 82px minmax(0, 1fr) auto;
    gap: 9px;
    align-items: flex-start;
    padding: 12px 14px 11px;
    background: #f4f6f8;
    border: 1px solid #e9edf3;
    border-bottom: 2px solid $main-new;
    border-radius: 10px 10px 2px 2px;
}

.act-script-card--invalid {
    border-color: #f0b4af;
    border-bottom-color: #e0473a;
}

.act-variant-label {
    padding-top: 22px;
    color: #6f7788;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.2;
}

.act-text-wrap {
    min-width: 0;
}

.act-script-label {
    margin-bottom: 2px;
    color: $muted-new;
    font-size: 10.5px;
}

.act-textarea {
    display: block;
    width: 100%;
    min-height: 52px;
    padding: 4px 6px 5px 0;
    // Высоту задаёт autoGrow() по scrollHeight, поэтому скроллбар не нужен —
    // он бы искажал замер. Ручной ресайз отключён: его всё равно перезапишет
    // следующий ввод.
    overflow-y: hidden;
    color: #0f172a;
    font-family: Inter, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.45;
    resize: none;
    background: transparent;
    border: 0;
    border-radius: 8px;
    outline: none;
}

.act-textarea:focus {
    padding-left: 8px;
    background: #fff;
    box-shadow: inset 0 0 0 1px #d8dcf8, 0 0 0 3px rgba(82, 90, 213, 0.1);
}

.act-tools {
    position: relative;
    display: flex;
    align-items: center;
    padding-top: 18px;
}

.act-tool {
    padding: 2px 3px;
    color: #6f7788;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: 6px;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 800;
}

.act-tool:hover {
    color: $dark-new;
    background: #e9edf3;
}

.act-vars-popover {
    position: absolute;
    top: 42px;
    right: 0;
    z-index: 3;
    width: 230px;
    padding: 5px 0;
    overflow: hidden;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 9px;
    box-shadow: 0 10px 28px rgba(26, 26, 46, 0.18);
}

.act-var-option {
    display: block;
    width: 100%;
    padding: 11px 18px;
    color: #526075;
    cursor: pointer;
    background: #fff;
    border: 0;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 12.5px;
    font-weight: 700;
    text-align: left;
}

.act-var-option:hover {
    color: $main-new;
    background: $bcg-card-new;
}

.act-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px;
    background: #fff;
    border-top: 1px solid $border-new;
}

.act-foot-note {
    color: $muted-new;
    font-size: 12.5px;
    line-height: 1.4;
}

.act-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
}

.act-ghost,
.act-primary {
    padding: 11px 17px;
    cursor: pointer;
    border-radius: 9px;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.03em;
    text-transform: uppercase;
}

.act-ghost {
    color: $text-color-new;
    background: #fff;
    border: 1.4px solid $border-new;
}

.act-primary-wrap {
    display: inline-flex;
}

.act-primary {
    color: #fff;
    background: $main-new;
    border: 1.4px solid $main-new;
    box-shadow: 0 2px 10px rgba(82, 90, 213, 0.26);
}

.act-primary:disabled {
    opacity: 0.58;
    cursor: not-allowed;
    box-shadow: none;
}

.activation-script-modal-enter-active,
.activation-script-modal-leave-active {
    transition: opacity 0.18s ease;
}

.activation-script-modal-enter,
.activation-script-modal-leave-to {
    opacity: 0;
}

@media (max-width: 860px) {
    .activation-overlay {
        padding: 8px;
    }

    .activation-modal {
        height: 88vh;
    }

    .act-main {
        padding: 16px;
    }

    .act-conditions--grid {
        grid-template-columns: 1fr;
    }

    .act-script-card {
        grid-template-columns: minmax(0, 1fr);
    }

    .act-variant-label,
    .act-tools {
        padding-top: 0;
    }

    .act-tools {
        justify-content: flex-start;
    }

    .act-vars-popover {
        right: auto;
        left: 0;
    }

    .act-footer {
        flex-direction: column;
        align-items: stretch;
    }

    .act-actions {
        justify-content: stretch;
    }

    .act-ghost,
    .act-primary,
    .act-primary-wrap {
        flex: 1;
    }
}
</style>
